// 스테이지 지도가 쓰는 세트피스 목록(three 없음 — 단위 테스트가 파일 존재를 확인한다).
// 지역1(S2): public/assets/r1/set_<이름>.glb. 지역2(S6): 이름이 e_ 로 시작하고 public/assets/r2/set_<e_ 뺀 이름>.glb(tools/blender/r2_stage_sets.py).
import type { LandmarkKind, MapDef } from '../../shared/levels';

/** 지역2 세트피스 이름(e_ + 파일 이름) */
export type R2SetName =
  | 'e_edge_col' | 'e_edge_col_b' | 'e_edge_chain' | 'e_edge_forge' | 'e_bridge' | 'e_gate' | 'e_brazier' | 'e_fire_bowl' | 'e_banner' | 'e_chain_span'
  | 'e_crucible' | 'e_anvil' | 'e_altar' | 'e_obelisk' | 'e_basalt' | 'e_rubble' | 'e_prop_rock' | 'e_prop_slag' | 'e_prop_crystal' | 'e_far_spire' | 'e_far_aqueduct';
export type SetName =
  | 'edge_stone' | 'edge_stone_b' | 'edge_parapet' | 'edge_root' | 'edge_palisade' | 'edge_quay'
  | 'bridge' | 'bridge_wood' | 'bridge_root' | 'ruin_arch' | 'ruin_arcade' | 'tower' | 'throne' | 'palisade_gate' | 'root_gate'
  | 'bonfire' | 'banner_pole' | 'bone_pile' | 'far_cliff' | 'far_aqueduct' | 'root_arc' | 'root_knot'
  | 'prop_rock' | 'prop_rubble' | 'prop_crates' | 'prop_mushrooms'
  | R2SetName;

/** 파일 목록(고정): 이 이름들이 public/assets/r1/set_<이름>.glb 이다 */
export const SET_NAMES: readonly SetName[] = [
  'edge_stone', 'edge_stone_b', 'edge_parapet', 'edge_root', 'edge_palisade', 'edge_quay',
  'bridge', 'bridge_wood', 'bridge_root', 'ruin_arch', 'ruin_arcade', 'tower', 'throne', 'palisade_gate', 'root_gate',
  'bonfire', 'banner_pole', 'bone_pile', 'far_cliff', 'far_aqueduct', 'root_arc', 'root_knot',
  'prop_rock', 'prop_rubble', 'prop_crates', 'prop_mushrooms',
];
/** 지역2 파일 목록(고정): public/assets/r2/set_<e_ 뺀 이름>.glb */
export const R2_SET_NAMES: readonly R2SetName[] = [
  'e_edge_col', 'e_edge_col_b', 'e_edge_chain', 'e_edge_forge', 'e_bridge', 'e_gate', 'e_brazier', 'e_fire_bowl', 'e_banner', 'e_chain_span',
  'e_crucible', 'e_anvil', 'e_altar', 'e_obelisk', 'e_basalt', 'e_rubble', 'e_prop_rock', 'e_prop_slag', 'e_prop_crystal', 'e_far_spire', 'e_far_aqueduct',
];
/** 세트피스 이름 → 자산 경로(public/assets 아래) */
export function setFile(name: SetName): string {
  return name.startsWith('e_') ? 'r2/set_' + name.slice(2) + '.glb' : 'r1/set_' + name + '.glb';
}
/** 세트피스가 쓰는 공용 아틀라스 지역 */
export function setRegion(name: SetName): 'r1' | 'r2' {
  return name.startsWith('e_') ? 'r2' : 'r1';
}

/**
 * 표면 규칙 키(바닥·절벽·소품·빛): 지역1 a·b·m·f, 지역2 ea·eb·em·ef. 반전 지도(a2·b2)는 원래 지도 규칙을 쓴다.
 */
export type StageLookKey = 'a' | 'b' | 'm' | 'f' | 'ea' | 'eb' | 'em' | 'ef';
export function lookOf(map: MapDef): StageLookKey {
  const k = map.stageKey ?? 'a';
  const base = k === 'a' || k === 'a2' ? 'a' : k === 'b' || k === 'b2' ? 'b' : k === 'm' ? 'm' : 'f';
  return map.id === 'r2' ? (('e' + base) as StageLookKey) : base;
}
/** 지역2(용암) 표면인지 */
export function isEmberLook(k: StageLookKey): boolean {
  return k.charAt(0) === 'e';
}

/** 가장자리 모양 → 트림 조각 후보(표본마다 하나를 고른다) */
export const STYLE_MODULES: Record<string, SetName[]> = {
  cliff: ['edge_stone', 'edge_stone', 'edge_stone_b'],
  parapet: ['edge_parapet', 'edge_parapet', 'edge_stone'],
  palisade: ['edge_palisade'],
  roots: ['edge_root'],
  water: ['edge_quay'],
  basalt: ['e_edge_col', 'e_edge_col', 'e_edge_col_b'],
  chain: ['e_edge_chain'],
  forge: ['e_edge_forge', 'e_edge_forge', 'e_edge_col'],
};

/** 지도별 낭떠러지 단면 아케이드 간격(표본 수, 2m 간격 표본 기준 — 0이면 없음). 지역1 A만 */
export const ARCADE_EVERY: Readonly<Record<string, number>> = { a: 10, a2: 10 };
export function arcadeEveryOf(map: MapDef): number {
  return map.id === 'r1' ? ARCADE_EVERY[map.stageKey ?? ''] ?? 0 : 0;
}

/** 가장자리 곁 소품(표면 규칙 키별): 세트 이름과 높이 범위(m) — 인스턴싱 저폴리 조각 */
export const SCATTER_SETS: Readonly<Record<StageLookKey, readonly { kind: SetName; h: readonly [number, number] }[]>> = {
  a: [{ kind: 'prop_rubble', h: [0.4, 0.7] }, { kind: 'prop_rock', h: [0.4, 0.7] }],
  b: [{ kind: 'prop_crates', h: [0.9, 1.2] }, { kind: 'prop_rubble', h: [0.4, 0.6] }],
  m: [{ kind: 'prop_crates', h: [0.9, 1.3] }, { kind: 'prop_rock', h: [0.4, 0.8] }, { kind: 'prop_rubble', h: [0.4, 0.6] }],
  f: [{ kind: 'prop_mushrooms', h: [0.7, 1.0] }, { kind: 'prop_rock', h: [0.4, 0.8] }],
  ea: [{ kind: 'e_prop_rock', h: [0.4, 0.8] }, { kind: 'e_prop_slag', h: [0.3, 0.5] }, { kind: 'e_prop_crystal', h: [0.6, 1.0] }],
  eb: [{ kind: 'e_prop_rock', h: [0.4, 0.7] }, { kind: 'e_prop_slag', h: [0.3, 0.5] }],
  em: [{ kind: 'e_prop_slag', h: [0.35, 0.6] }, { kind: 'e_prop_rock', h: [0.4, 0.7] }],
  ef: [{ kind: 'e_prop_crystal', h: [0.7, 1.2] }, { kind: 'e_prop_rock', h: [0.4, 0.7] }],
};

/**
 * 랜드마크(충돌 사각형) → 세트피스. 지역1은 기둥·잔해·여신상을 키트로 그리고 망루·모닥불·왕좌·뿌리만 세트,
 * 지역2는 모두 세트(현무암 기둥 무리·화로 탑·세발 화로·도가니·모루·제단·오벨리스크). null이면 키트·그레이박스
 */
export function landmarkSet(map: MapDef, kind: LandmarkKind): SetName | null {
  if (map.id === 'r2') {
    switch (kind) {
      case 'basalt': return 'e_basalt';
      case 'rubble': return 'e_rubble';
      case 'tower': return 'e_brazier';
      case 'bonfire': return 'e_fire_bowl';
      case 'pillar': return 'e_obelisk';
      case 'crucible': case 'throne': return 'e_crucible';
      case 'anvil': return 'e_anvil';
      case 'altar': return 'e_altar';
      default: return null;
    }
  }
  if (kind === 'tower' || kind === 'bonfire' || kind === 'throne') return kind;
  if (kind === 'root') return 'root_knot';
  return null;
}

/** 이 지도가 쓰는 세트피스 이름(앵커 + 가장자리 모양별 트림 + 랜드마크) */
export function usedSets(map: MapDef): Set<SetName> {
  const s = new Set<SetName>();
  for (const p of map.sets ?? []) if (p.kind !== 'waterfall' && p.kind !== 'lavafall') s.add(p.kind as SetName);
  const styles = new Set<string>([map.edgeStyle ?? 'cliff']);
  for (const z of map.edgeZones ?? []) styles.add(z.style);
  for (const st of styles) for (const n of STYLE_MODULES[st] ?? []) s.add(n);
  if (arcadeEveryOf(map) > 0) s.add('ruin_arcade');
  for (const sc of SCATTER_SETS[lookOf(map)]) s.add(sc.kind);
  for (const l of map.landmarks) { const n = landmarkSet(map, l.kind); if (n) s.add(n); }
  return s;
}
